import {
  HStack,
  Box,
  Image,
  Text,
  Flex,
  useMediaQuery,
  PaperlessCubeIcon,
} from '@frontend/components';

import { INTEGRATIONS } from 'modules/settings/constants';

const ManagedIntegrationCard = ({
  imageUrl,
  name,
  description,
  onClick = undefined,
  children,
  ref = undefined,
  author = 'Paperless Parts',
  developedByPaperlessParts = false,
}) => {
  const [isMobile] = useMediaQuery('(max-width: 640px)');
  const [min] = useMediaQuery('(min-width: 900px)');
  const [max] = useMediaQuery('(max-width: 955px)');
  const isEdgeCase = min && max;
  const useSmallCard = isEdgeCase || isMobile;

  return (
    <Card name={name} onClick={onClick} ref={ref}>
      <ResponsiveStack
        useSmallCard={useSmallCard}
        padding={useSmallCard ? '16px 16px 12px 20px' : '0px 0px 0px 12px'}
      >
        <ResponsiveStack useSmallCard={useSmallCard}>
          <LogoImage imageUrl={imageUrl} />
          <Divider useSmallCard={useSmallCard} />
        </ResponsiveStack>

        <Flex flexDirection="column" alignItems="left">
          <HStack justifyContent="left" spacing="4px" textAlign="left">
            <Text
              size="lg"
              color={name ? 'feature.primary' : 'feature.placeholder'}
              fontWeight="heavy"
              wordBreak="break-all"
            >
              {name || '(Software name)'}
            </Text>

            <Text size="md" color="feature.secondary">
              by
            </Text>

            <Author
              developedByPaperlessParts={developedByPaperlessParts}
              author={author}
            />
          </HStack>

          <Text
            color={description ? 'feature.secondary' : 'feature.placeholder'}
            noOfLines={1}
            textAlign="left"
          >
            {description || '(Integration description)'}
          </Text>
        </Flex>
      </ResponsiveStack>

      {children}
    </Card>
  );
};

const ResponsiveStack = ({ useSmallCard, children, ...restProps }) => {
  if (useSmallCard) {
    return (
      <Flex flexDirection="column" {...restProps}>
        {children}
      </Flex>
    );
  }
  return <HStack {...restProps}>{children}</HStack>;
};

const Divider = ({ useSmallCard, children }) => {
  const height = useSmallCard ? '1px' : '52px';
  const width = useSmallCard ? '120px' : '1px';
  const padding = () => ({
    paddingX: useSmallCard ? '0px' : '8px',
    paddingY: useSmallCard ? '12px' : '0px',
  });

  return (
    <Box {...padding()}>
      <Box height={height} width={width} bgColor="separator.border">
        {children}
      </Box>
    </Box>
  );
};

const Author = ({ developedByPaperlessParts, author = 'Paperless Parts' }) => {
  if (!developedByPaperlessParts) {
    return (
      <Text
        color={author ? 'feature.secondary' : 'feature.placeholder'}
        fontWeight="default"
      >
        {author || 'Author'}
      </Text>
    );
  }

  return (
    <Flex align="center">
      <Text size="md" fontWeight="heavy" color="brand.secondary" noOfLines={1}>
        Paperless Parts
      </Text>
      <PaperlessCubeIcon
        marginLeft="2px"
        width="22px"
        height="22px"
        color={'brand.secondary'}
      />
    </Flex>
  );
};

const LogoImage = ({ imageUrl }) => (
  <Image
    src={imageUrl}
    width="120px"
    minWidth="120px"
    height="50px"
    objectFit="contain"
    alt="Logo"
  />
);

const integrationCardStyles = (onClick) => ({
  backgroundColor: '#fff',
  minHeight: '80px',
  width: '100%',
  borderRadius: '8px',
  border: '1px',
  borderColor: 'separator.border',
  marginBottom: '24px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  transitionDuration: 'normal',
  transitionProperty: 'common',
  cursor: onClick ? 'pointer' : 'default',

  _hover: {
    backgroundColor: onClick ? 'interaction.surfaceHover' : '',
  },
  _active: {
    backgroundColor: onClick ? 'interaction.surfaceActive' : '',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
});

const Card = ({ name, onClick, children, ref }) => (
  <Box
    data-fs-id="open-managed-integration-drawer-settings"
    {...integrationCardStyles(onClick)}
    ref={ref}
    as={name === INTEGRATIONS.QUICKBOOKS_CARD_NAME ? '' : 'button'}
    onClick={onClick}
  >
    {children}
  </Box>
);

export default ManagedIntegrationCard;
